import _styled, { default as _styled2, default as _styled3 } from "styled-components";
var _templateObject, _templateObject2;
const _excluded = ["style", "className", "id", "name", "checked", "disabled", "value", "onChange", "inputProps", "data-testid"],
  _excluded2 = ["style"];
function _taggedTemplateLiteral(strings, raw) { if (!raw) { raw = strings.slice(0); } return Object.freeze(Object.defineProperties(strings, { raw: { value: Object.freeze(raw) } })); }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } } return target; }
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import theme, { getInteractiveStyles, stylePropType } from '@adsk/alloy-react-theme';
import RadioGroupContext from './RadioGroupContext';
/**
 * Provides a list of choices (radio buttons) within a form when only one item may be selected at a time.
 */
const RadioButton = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      id,
      name: nameProp,
      checked: checkedProp,
      disabled = false,
      value,
      onChange: onChangeProp,
      inputProps = {},
      'data-testid': dataTestid
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const radioGroup = useContext(RadioGroupContext);
  let checked = checkedProp;
  let name = nameProp;
  if (radioGroup) {
    if (typeof checked === 'undefined') {
      checked = radioGroup.value === value;
    }
    if (typeof name === 'undefined') {
      name = radioGroup.name;
    }
  }
  const handleChange = e => {
    if (onChangeProp) {
      onChangeProp(value, e);
    } else if (radioGroup) {
      var _radioGroup$onChange;
      (_radioGroup$onChange = radioGroup.onChange) === null || _radioGroup$onChange === void 0 ? void 0 : _radioGroup$onChange.call(radioGroup, value, e);
    }
  };
  const {
      style: inputStyle
    } = inputProps,
    rest = _objectWithoutProperties(inputProps, _excluded2);
  return /*#__PURE__*/React.createElement(_StyledDiv, _extends({
    role: "radio",
    "aria-checked": checked,
    className: className
  }, props, {
    $_css: [_objectSpread({
      display: 'block',
      position: 'relative',
      height: '16px',
      width: '16px',
      borderWidth: checked ? '0px' : '1px',
      borderStyle: 'solid',
      borderColor: disabled ? theme.tokens.colors.border.inactive.value : theme.tokens.colors.border.value,
      borderRadius: theme.borderRadius.circle,
      cursor: 'pointer'
    }, getInteractiveStyles(disabled)), style]
  }), checked && /*#__PURE__*/React.createElement(_StyledDiv2, {
    $_css2: [{
      borderRadius: theme.borderRadius.circle,
      width: '100%',
      height: '100%',
      boxShadow: "inset 0 0 0 4px ".concat(disabled ? theme.tokens.colors.background.neutral.inactive.value : theme.tokens.colors.background.selected.value)
    }]
  }), /*#__PURE__*/React.createElement(_StyledInput, _extends({
    ref: ref,
    id: id,
    name: name,
    type: "radio",
    value: value,
    checked: checked,
    disabled: disabled,
    onChange: disabled ? undefined : handleChange,
    "data-testid": dataTestid
  }, rest, {
    $_css3: [{
      cursor: 'inherit',
      position: 'absolute',
      opacity: 0,
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      margin: 0,
      padding: 0,
      zIndex: 1,
      '&:before': {
        content: "' '",
        position: 'absolute',
        width: 20,
        height: 20,
        top: checked ? '-2px' : '-3px',
        left: checked ? '-2px' : '-3px'
      },
      // pointer-events needs to be 'none' when disabled for a wrapping
      // Tooltip to disappear correctly: https://github.com/react-component/tooltip/issues/18#issuecomment-411476678
      pointerEvents: disabled ? 'none' : 'auto'
    }, inputStyle]
  })));
});
RadioButton.displayName = 'RadioButton';
RadioButton.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The id of the radio, used for the label */
  id: PropTypes.string,
  /** The name for the radio input */
  name: PropTypes.string,
  /** The value of the radio */
  checked: PropTypes.bool,
  /** The value of the radio */
  value: PropTypes.any,
  /** onChange handler for the radio input */
  onChange: PropTypes.func,
  /** Disable radio functionality */
  disabled: PropTypes.bool,
  /** Props passed to the input element */
  inputProps: PropTypes.object,
  /** data-testid passed to the input element */
  'data-testid': PropTypes.string
};
export default RadioButton;
var _StyledDiv = _styled("div")(_templateObject || (_templateObject = _taggedTemplateLiteral(["", ""])), p => p.$_css);
var _StyledDiv2 = _styled2("div")(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["", ""])), p => p.$_css2);
var _StyledInput = _styled3("input").withConfig({
  displayName: "RadioButton___StyledInput",
  componentId: "sc-ic8omn-0"
})(["", ""], p => p.$_css3);
//# sourceMappingURL=RadioButton.js.map